import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Modal, Field, Empty } from "@/components/Kit";
import { Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = { name: "", code: "", max_fa: 50, max_sa: 100 };

export default function SubjectsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { data: subjects = [] } = useQuery({ queryKey: ["subjects"], queryFn: async () => (await api.get("/school/academics/subjects")).data });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, max_fa: Number(form.max_fa), max_sa: Number(form.max_sa), classes: [] };
      return editing ? (await api.put(`/school/academics/subjects/${editing.id}`, body)).data : (await api.post("/school/academics/subjects", body)).data;
    },
    onSuccess: () => { toast.success("Subject saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["subjects"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/academics/subjects/${id}`)).data,
    onSuccess: () => { toast.success("Subject removed"); qc.invalidateQueries({ queryKey: ["subjects"] }); },
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="subjects-tab">
      <div className="mb-4 flex justify-end"><button className="ep-btn" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-subject-btn"><Plus size={16} /> Add Subject</button></div>
      <div className="ep-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Code</th><th className="px-4 py-3 text-right">FA Max</th><th className="px-4 py-3 text-right">SA Max</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {subjects.map((s) => (
              <tr key={s.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`subject-row-${s.code || s.name}`}>
                <td className="px-4 py-3 font-medium text-white">{s.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.code || "—"}</td>
                <td className="px-4 py-3 text-right text-slate-300">{s.max_fa}</td>
                <td className="px-4 py-3 text-right text-slate-300">{s.max_sa}</td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1.5">
                  <button onClick={() => { setEditing(s); setForm(s); setOpen(true); }} className="rounded-lg bg-slate-700/40 p-2 text-slate-300 hover:bg-slate-700/70" data-testid={`edit-subject-${s.code || s.name}`}><Pencil size={14} /></button>
                  <button onClick={() => remove.mutate(s.id)} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" data-testid={`del-subject-${s.code || s.name}`}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
            {!subjects.length && <tr><td colSpan={5}><Empty>No subjects yet</Empty></td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Subject" : "Add Subject"} testid="subject-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject Name *" value={form.name} onChange={upd("name")} data-testid="sub-name" />
          <Field label="Code" value={form.code} onChange={upd("code")} placeholder="ENG" data-testid="sub-code" />
          <Field label="FA Max Marks" type="number" value={form.max_fa} onChange={upd("max_fa")} data-testid="sub-max-fa" />
          <Field label="SA Max Marks" type="number" value={form.max_sa} onChange={upd("max_sa")} data-testid="sub-max-sa" />
        </div>
        <button className="ep-btn mt-5 w-full" disabled={save.isPending || !form.name} onClick={() => save.mutate()} data-testid="save-subject-btn">{save.isPending ? "Saving…" : "Save Subject"}</button>
      </Modal>
    </div>
  );
}
